import axios from "axios";
import * as Sentry from "@sentry/react";

// Extend Axios config to include request timing metadata
declare module "axios" {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
      sentrySpan?: ReturnType<typeof Sentry.startInactiveSpan>;
    };
  }
}

// API Performance Metrics
interface ApiMetric {
  method: string;
  url: string;
  status: number | string;
  duration: number;
  timestamp: Date;
}

const metrics: ApiMetric[] = [];
const maxMetrics = 100;

export const apiMetrics = {
  add(metric: ApiMetric) {
    metrics.push(metric);
    if (metrics.length > maxMetrics) {
      metrics.shift();
    }
  },

  getSummary() {
    if (metrics.length === 0) return null;

    const sorted = [...metrics].sort((a, b) => b.duration - a.duration);
    const avgDuration =
      metrics.reduce((sum, m) => sum + m.duration, 0) / metrics.length;

    const byEndpoint = metrics.reduce(
      (acc, m) => {
        const key = `${m.method} ${m.url}`;
        if (!acc[key]) {
          acc[key] = { count: 0, totalDuration: 0, maxDuration: 0 };
        }
        acc[key].count++;
        acc[key].totalDuration += m.duration;
        acc[key].maxDuration = Math.max(acc[key].maxDuration, m.duration);
        return acc;
      },
      {} as Record<
        string,
        { count: number; totalDuration: number; maxDuration: number }
      >,
    );

    return {
      totalRequests: metrics.length,
      avgDuration: avgDuration.toFixed(2),
      slowest: sorted.slice(0, 5),
      fastest: sorted.slice(-5).reverse(),
      byEndpoint,
    };
  },

  getMetrics() {
    return [...metrics];
  },
};

import { useCreatePOA } from "./useCreatePOA";
import { useCreateUser } from "./useCreateUser";
import { useGetFetchPOAText } from "./useFetchPOAText";
import { useGetLoginStatus } from "./useGetLoginStatus";
import { useGetSignStatus } from "./useGetSignStatus";
import { useSignPOA } from "./useSignPOA";
import { useStartLogin } from "./useStartLogin";
import { useStartSignIn } from "./useCustomerSignIn";
import { useStartSignUp } from "./useCustomerSignUp";
import { usePageViewTracking } from "./usePageviewTracking";
import { useCreateBooking } from "./useCreateBooking";
import { useGetBookingStatus } from "./useGetBookingStatus";
import { useGetUserById } from "./useGetUser";
import {
  useGetCustomerInfoById,
  getCustomerQueryOptions,
} from "./useGetCustomer";
import { useSignOpenPolicy } from "./useSignOpenPolicy";
import { useCreateOpenPolicy } from "./useCreateOpenPolicy";
import { useGetDocumentHistoryById } from "./useGetDocumentHistoryById";
import { useGetAdviceById, getAdviceQueryOptions } from "./useGetAdviceById";
import { useGetOpenPolicy } from "./useGetOpenPolicy";
import { signApiRequest } from "./signApiRequest";
import {
  useGetCalculationById,
  getCalculationQueryOptions,
} from "./useGetCalculationById";
import {
  useGetTransferPolicy,
  getTransferPolicyQueryOptions,
} from "./useGetTransferPolicy";

import { useGetPoaStatus, getPoaStatus } from "./useGetPoaStatus";
import { usePatchCustomerSalary } from "./usePatchCustomerSalary";

import { useCancelTransaction } from "./useCancelTransaction";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

apiClient.defaults.timeout = 30000;

// Request timing interceptor
apiClient.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: performance.now() };

    // Start a Sentry span for this request
    const span = Sentry.startInactiveSpan({
      name: `${config.method?.toUpperCase()} ${config.url}`,
      op: "http.client",
      attributes: {
        "http.method": config.method?.toUpperCase(),
        "http.url": config.url,
      },
    });
    config.metadata.sentrySpan = span;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => {
    const endTime = performance.now();
    const startTime = response.config.metadata?.startTime;
    const duration = startTime ? endTime - startTime : 0;
    const method = response.config.method?.toUpperCase() || "UNKNOWN";
    const url = response.config.url || "unknown";

    // End Sentry span
    const span = response.config.metadata?.sentrySpan;
    if (span) {
      span.setAttributes({
        "http.status_code": response.status,
        "http.response_content_length": response.headers?.["content-length"],
      });
      span.end();
    }

    apiMetrics.add({
      method,
      url,
      status: response.status,
      duration,
      timestamp: new Date(),
    });

    return response;
  },
  (error) => {
    const endTime = performance.now();
    const startTime = error.config?.metadata?.startTime;
    const duration = startTime ? endTime - startTime : 0;
    const method = error.config?.method?.toUpperCase() || "UNKNOWN";
    const url = error.config?.url || "unknown";
    const status = error.response?.status || "ERROR";

    // End Sentry span with error status
    const span = error.config?.metadata?.sentrySpan;
    if (span) {
      span.setAttributes({
        "http.status_code": status,
      });
      span.setStatus({ code: 2, message: "error" });
      span.end();
    }

    apiMetrics.add({
      method,
      url,
      status,
      duration,
      timestamp: new Date(),
    });

    return Promise.reject(error);
  },
);

export {
  apiClient,
  useCancelTransaction,
  signApiRequest,
  useCreateBooking,
  useCreatePOA,
  useCreateUser,
  useGetBookingStatus,
  useGetFetchPOAText,
  useGetLoginStatus,
  useGetSignStatus,
  usePageViewTracking,
  useSignPOA,
  useStartLogin,
  useStartSignIn,
  useStartSignUp,
  useGetUserById,
  useGetCustomerInfoById,
  getCustomerQueryOptions,
  useCreateOpenPolicy,
  useSignOpenPolicy,
  useGetDocumentHistoryById,
  useGetAdviceById,
  getAdviceQueryOptions,
  useGetOpenPolicy,
  useGetCalculationById,
  getCalculationQueryOptions,
  useGetTransferPolicy,
  getTransferPolicyQueryOptions,
  useGetPoaStatus,
  getPoaStatus,
  usePatchCustomerSalary,
};
